/**
 * EscapeSignatureCRUD — K4 · Admin Catalogue · Signature Escape.
 *
 * Founder ruling 16 Feb 2026 PM (verbatim · K4.1 approved):
 *   "Build K4.1 — Escape CRUD UI — to full parity with the K3 (Table)
 *    pattern. Same shape as everything else in the catalogue admin:
 *    List + search/filter, Create, Read, Edit (versioned/If-Match),
 *    Publish (through the gate), Archive, Restore, Image upload — the
 *    same CRUD surface K1/K2/K3 have, so the desk can hand-manage the
 *    43 Escape rows the same way. Reuse the existing components (the
 *    K3 TableSignature CRUD shape, the shared ImageUploader, the
 *    publish-gate drawer) — don't build new patterns, mirror what's
 *    proven. Flag-gated like the others (CATALOGUE_K4_ENABLED).
 *    Backward-compat: the 41 pre-Cloudinary Escape rows use
 *    hero_image_url — make sure the UI renders and validates those,
 *    doesn't break them."
 *
 * Sibling of TableSignatureCRUD (K3) + EnjoySignatureCRUD (K1)
 *  + EnjoyOverlayCRUD (K2). Same UX shape. K4-specific fields
 * (destination, region, experience_type, season_best_months, duration,
 * supplier_name, supplier_url, safety_level, safety_notes,
 * visa_or_permit_notes, age_fitness_notes). NO Michelin chip, NO booking
 * rails, NO force_concierge (those are K3 restaurant-shaped concerns),
 * NO clone endpoint (K4 backend doesn't expose one), NO backfill panel
 * (K3.1-specific).
 *
 * Reuses K1's ImageUploader.jsx verbatim. The 41 legacy rows have
 * `hero_image_url` (Unsplash curated stock) but no `image_url` / no
 * `image_public_id`. The detail view renders `image_url || hero_image_url`
 * so legacy rows show their image · uploading a new image goes through
 * `/image/upload` and the backend mirrors `hero_image_url ← image_url`
 * (see catalogue_escape.py doctrine point #6).
 *
 * Backend endpoints used:
 *   GET    /api/os-admin/catalogue/escape-signature
 *   GET    /api/os-admin/catalogue/escape-signature/{id}
 *   POST   /api/os-admin/catalogue/escape-signature
 *   PATCH  /api/os-admin/catalogue/escape-signature/{id}    (If-Match: "<version>")
 *   POST   /api/os-admin/catalogue/escape-signature/{id}/publish
 *   POST   /api/os-admin/catalogue/escape-signature/{id}/archive
 *   POST   /api/os-admin/catalogue/escape-signature/{id}/restore
 *   POST   /api/os-admin/catalogue/escape-signature/{id}/image/upload
 *   DELETE /api/os-admin/catalogue/escape-signature/{id}/image
 *   GET    /api/os-admin/catalogue/escape-signature/{id}/audit
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/pages/admin/os/ImageUploader";

const ROOT = "/os-admin/catalogue/escape-signature";

const STATUS_CHIP = {
    draft: { bg: "#fef3c7", fg: "#78350f", label: "Draft" },
    live: { bg: "#dcfce7", fg: "#14532d", label: "Live" },
    archived: { bg: "#e5e7eb", fg: "#374151", label: "Archived" },
};

const SAFETY_LEVELS = [
    { value: "", label: "—" },
    { value: "low", label: "Low" },
    { value: "moderate", label: "Moderate" },
    { value: "high", label: "High" },
    { value: "extreme", label: "Extreme" },
    { value: "desk_review_required", label: "Desk review required" },
];

const DESK_REVIEW_STATUSES = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rework", label: "Rework" },
    { value: "blocked", label: "Blocked" },
];

const inputStyle = {
    width: "100%", padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 4,
    fontSize: 14, boxSizing: "border-box",
    background: "#fff", color: "#1a2332",
};


function StatusChip({ status }) {
    const c = STATUS_CHIP[status] || STATUS_CHIP.draft;
    return (
        <span
            data-testid={`k4-status-chip-${status}`}
            style={{
                background: c.bg, color: c.fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
                letterSpacing: "0.05em", textTransform: "uppercase",
            }}
        >{c.label}</span>
    );
}


function SafetyChip({ level }) {
    if (!level) return null;
    const palette = {
        low: { bg: "#dcfce7", fg: "#14532d" },
        moderate: { bg: "#fef3c7", fg: "#78350f" },
        high: { bg: "#fed7aa", fg: "#9a3412" },
        extreme: { bg: "#fecaca", fg: "#991b1b" },
        desk_review_required: { bg: "#dbeafe", fg: "#1e40af" },
    };
    const p = palette[level] || palette.moderate;
    return (
        <span
            data-testid={`k4-safety-chip-${level}`}
            style={{
                background: p.bg, color: p.fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
                marginRight: 4,
            }}
        >Safety · {level.replace(/_/g, " ")}</span>
    );
}


function Field({ label, testid, children }) {
    return (
        <label data-testid={`k4-field-${testid}`} style={{ display: "block", marginBottom: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                {label}
            </span>
            {children}
        </label>
    );
}


function ListView({ items, total, q, onSearchChange, statusFilter, onStatusChange, onOpen, onCreate, loading, flagDisabled }) {
    return (
        <div data-testid="k4-list-view">
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }} data-testid="k4-title">
                    Escape Signature · Catalogue
                </h1>
                <span style={{ color: "#666", fontSize: 13 }}>
                    {total} {total === 1 ? "row" : "rows"}
                </span>
                <div style={{ flex: 1 }} />
                <input
                    type="search"
                    placeholder="Search title / destination / region / supplier"
                    value={q}
                    onChange={(e) => onSearchChange(e.target.value)}
                    data-testid="k4-search"
                    style={{ ...inputStyle, width: 320 }}
                />
                <select
                    value={statusFilter || ""}
                    onChange={(e) => onStatusChange(e.target.value || null)}
                    data-testid="k4-status-filter"
                    style={{ ...inputStyle, width: 140 }}
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="live">Live</option>
                    <option value="archived">Archived</option>
                </select>
                <button
                    type="button"
                    onClick={onCreate}
                    data-testid="k4-create-btn"
                    style={{
                        background: "#111827", color: "white", border: 0,
                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                        fontWeight: 600, cursor: "pointer",
                    }}
                >+ New escape signature</button>
            </div>

            {flagDisabled ? (
                <div data-testid="k4-flag-disabled-banner" style={{
                    padding: 12, marginBottom: 16, borderRadius: 4,
                    background: "#fef3c7", color: "#78350f", fontSize: 13,
                }}>
                    K4 (Escape Signature CRUD) is not enabled in this environment.
                    Set <code>CATALOGUE_K4_ENABLED=true</code> in
                    <code> /app/backend/.env</code> and restart the backend.
                </div>
            ) : null}
            {loading ? <div data-testid="k4-loading">Loading…</div> : null}
            <div data-testid="k4-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
            }}>
                {items.map(it => {
                    // K4 backward-compat: 41 pre-Cloudinary rows have hero_image_url
                    // but no image_url. Render whichever is present.
                    const thumb = it.image_url || it.hero_image_url || null;
                    const isLegacyImage = !it.image_url && !!it.hero_image_url;
                    return (
                        <button
                            key={it.id}
                            type="button"
                            onClick={() => onOpen(it.id)}
                            data-testid={`k4-row-${it.id}`}
                            style={{
                                textAlign: "left", padding: 14, border: "1px solid #e5e7eb",
                                borderRadius: 6, background: "white", color: "#1a2332", cursor: "pointer",
                                display: "flex", flexDirection: "column", gap: 8,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <StatusChip status={it.publish_status || "draft"} />
                                <SafetyChip level={it.safety_level} />
                                <span style={{ fontSize: 11, color: "#9ca3af" }}>v{it.version || 1}</span>
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>
                                {it.title || <i>(untitled)</i>}
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {it.experience_type ? <>{it.experience_type} · </> : null}
                                {[it.destination, it.country].filter(Boolean).join(", ")}
                            </div>
                            {thumb ? (
                                <div style={{ position: "relative" }}>
                                    <img
                                        src={thumb} alt=""
                                        data-testid={`k4-row-${it.id}-thumb`}
                                        style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4 }}
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                    {isLegacyImage ? (
                                        <div
                                            data-testid={`k4-row-${it.id}-legacy-img-badge`}
                                            title="Pre-K4 Unsplash curated stock image. Upload a new image to migrate this row to Cloudinary."
                                            style={{
                                                position: "absolute", top: 4, right: 4,
                                                fontSize: 9, fontWeight: 600, letterSpacing: 0.4,
                                                color: "#fff", background: "rgba(15,23,42,0.65)",
                                                padding: "2px 5px", borderRadius: 3,
                                            }}
                                        >
                                            LEGACY IMG
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div style={{
                                    fontSize: 11, color: "#92400e",
                                    background: "#fef3c7", padding: "4px 6px", borderRadius: 4,
                                }}>No image · cannot publish</div>
                            )}
                        </button>
                    );
                })}
                {(!items.length && !loading) ? (
                    <div data-testid="k4-empty" style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>
                        No rows match your filter.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


function CreateForm({ onCreated, onCancel }) {
    const [draft, setDraft] = useState({
        title: "", destination: "", country: "",
        experience_type: "", concierge_whisper: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const handle = (k, v) => setDraft(d => ({ ...d, [k]: v }));

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const body = Object.fromEntries(
                Object.entries(draft).filter(([_, v]) => v && String(v).trim()),
            );
            const r = await api.post(ROOT, body);
            onCreated(r.data.id);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "validation_failed") {
                setErr("Validation: " + (detail.violations || []).join(" · "));
            } else if (detail?.error === "forbidden") {
                setErr("Founder role required to create new escape rows.");
            } else {
                setErr(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="k4-create-form" style={{ maxWidth: 560 }}>
            <h2 style={{ marginTop: 0 }}>New escape signature</h2>
            {err ? (
                <div data-testid="k4-create-error" style={{
                    padding: 8, background: "#fdecea", color: "#a02020",
                    borderRadius: 4, marginBottom: 8, fontSize: 13,
                }}>{err}</div>
            ) : null}
            <Field label="Title" testid="title">
                <input value={draft.title} onChange={e => handle("title", e.target.value)}
                       data-testid="k4-create-title" style={inputStyle} />
            </Field>
            <Field label="Destination" testid="destination">
                <input value={draft.destination} onChange={e => handle("destination", e.target.value)}
                       data-testid="k4-create-destination" style={inputStyle}
                       placeholder="e.g. Antarctic Peninsula" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Country (ISO-2)" testid="country">
                    <input value={draft.country} onChange={e => handle("country", e.target.value.toUpperCase())}
                           maxLength={3} data-testid="k4-create-country" style={inputStyle} />
                </Field>
                <Field label="Experience type" testid="experience_type">
                    <input value={draft.experience_type}
                           onChange={e => handle("experience_type", e.target.value)}
                           data-testid="k4-create-experience-type" style={inputStyle}
                           placeholder="e.g. Expedition cruise" />
                </Field>
            </div>
            <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                <textarea value={draft.concierge_whisper} maxLength={280}
                          onChange={e => handle("concierge_whisper", e.target.value)}
                          data-testid="k4-create-whisper"
                          style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submit} disabled={busy || !draft.title || !draft.destination}
                        data-testid="k4-create-submit"
                        style={{
                            background: "#111827", color: "white", border: 0,
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            fontWeight: 600, cursor: "pointer",
                            opacity: (busy || !draft.title || !draft.destination) ? 0.5 : 1,
                        }}>
                    {busy ? "Creating…" : "Create draft"}
                </button>
                <button onClick={onCancel} data-testid="k4-create-cancel"
                        style={{
                            background: "white", color: "#374151",
                            border: "1px solid #d1d5db",
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            cursor: "pointer",
                        }}>Cancel</button>
            </div>
        </div>
    );
}


function DetailView({ rowId, onBack, onChanged }) {
    const [row, setRow] = useState(null);
    const [draft, setDraft] = useState({});
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [audit, setAudit] = useState([]);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get(`${ROOT}/${rowId}`);
            setRow(r.data);
            setDraft({ ...r.data });
            setDirty(false);
            try {
                const a = await api.get(`${ROOT}/${rowId}/audit`);
                setAudit(a.data.items || []);
            } catch (_) {
                // Audit failures are non-fatal · the detail view still renders.
                setAudit([]);
            }
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        }
    }, [rowId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleField = (k, v) => {
        setDraft(d => ({ ...d, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        if (!row || !dirty) return;
        setBusy(true); setError(null);
        try {
            const patch = {};
            Object.entries(draft).forEach(([k, v]) => {
                const a = row[k];
                const b = v;
                if (JSON.stringify(a ?? null) !== JSON.stringify(b ?? null)) {
                    patch[k] = v;
                }
            });
            if (!Object.keys(patch).length) {
                setDirty(false); setBusy(false); return;
            }
            await api.patch(`${ROOT}/${rowId}`, patch, {
                headers: { "If-Match": `"${row.version}"` },
            });
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "version_conflict") {
                setError("Row was edited by another operator. Refresh and re-apply.");
            } else if (detail?.error === "validation_failed") {
                setError("Validation failed: " + (detail.violations || []).join(" · "));
            } else if (detail?.error === "founder_only_field") {
                setError("That field is founder-only on the K4 OS shell. Use the legacy admin to edit it as a desk agent.");
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const callAction = async (action, label) => {
        if (!row) return;
        setBusy(true); setError(null);
        try {
            await api.post(`${ROOT}/${rowId}/${action}`);
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "publish_blocked") {
                const blocks = [
                    ...(detail.blocks || []),
                    ...(detail.gate_missing_fields || []).map(f => `${f}`),
                ];
                setError(`Publish blocked: ${blocks.join(" · ")}`);
            } else {
                setError(formatApiError(detail) || e.message || `${label} failed`);
            }
        } finally { setBusy(false); }
    };

    const handleImageUpload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`${ROOT}/${rowId}/image/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        await refresh();
        onChanged?.();
    };

    const handleImageRemove = async () => {
        await api.delete(`${ROOT}/${rowId}/image`);
        await refresh();
        onChanged?.();
    };

    if (!row) {
        return (
            <div data-testid="k4-detail-loading">
                <button onClick={onBack} data-testid="k4-detail-back-btn" style={{ marginBottom: 16 }}>← Back to list</button>
                {error ? <div style={{ color: "#a02020" }}>{error}</div> : <div>Loading…</div>}
            </div>
        );
    }

    // K4 backward-compat: 41 legacy rows have hero_image_url but no image_url.
    // The ImageUploader expects ONE currentImageUrl; render whichever is set.
    const displayImageUrl = row.image_url || row.hero_image_url || null;
    const isLegacyImage = !row.image_url && !!row.hero_image_url;

    return (
        <div data-testid="k4-detail-view">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={onBack} data-testid="k4-detail-back-btn"
                        style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, background: "white", cursor: "pointer" }}>
                    ← Back to list
                </button>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, flex: 1 }} data-testid="k4-detail-title">
                    {row.title || <i>Untitled</i>}
                </h1>
                <SafetyChip level={row.safety_level} />
                <StatusChip status={row.publish_status || "draft"} />
                <span style={{ fontSize: 12, color: "#999" }}>v{row.version || 1}</span>
            </div>

            {error ? (
                <div data-testid="k4-detail-error" style={{
                    padding: 10, marginBottom: 12, borderRadius: 4,
                    background: "#fdecea", color: "#a02020",
                }}>{error}</div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
                {/* LEFT · the form */}
                <div data-testid="k4-form">
                    <Field label="Title" testid="title">
                        <input value={draft.title || ""} onChange={e => handleField("title", e.target.value)}
                               data-testid="k4-input-title" style={inputStyle} />
                    </Field>
                    <Field label="Destination" testid="destination">
                        <input value={draft.destination || ""}
                               onChange={e => handleField("destination", e.target.value)}
                               data-testid="k4-input-destination" style={inputStyle} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Country (ISO-2)" testid="country">
                            <input value={draft.country || ""}
                                   onChange={e => handleField("country", e.target.value.toUpperCase())}
                                   maxLength={3} data-testid="k4-input-country" style={inputStyle} />
                        </Field>
                        <Field label="Region" testid="region">
                            <input value={draft.region || ""}
                                   onChange={e => handleField("region", e.target.value)}
                                   data-testid="k4-input-region" style={inputStyle} />
                        </Field>
                        <Field label="Experience type" testid="experience_type">
                            <input value={draft.experience_type || ""}
                                   onChange={e => handleField("experience_type", e.target.value)}
                                   data-testid="k4-input-experience-type" style={inputStyle} />
                        </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="Season (best months)" testid="season_best_months">
                            <input value={draft.season_best_months || ""}
                                   onChange={e => handleField("season_best_months", e.target.value)}
                                   data-testid="k4-input-season" style={inputStyle}
                                   placeholder="Nov–Mar" />
                        </Field>
                        <Field label="Duration" testid="duration">
                            <input value={draft.duration || ""}
                                   onChange={e => handleField("duration", e.target.value)}
                                   data-testid="k4-input-duration" style={inputStyle}
                                   placeholder="8–14 days" />
                        </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="Supplier name" testid="supplier_name">
                            <input value={draft.supplier_name || ""}
                                   onChange={e => handleField("supplier_name", e.target.value)}
                                   data-testid="k4-input-supplier-name" style={inputStyle} />
                        </Field>
                        <Field label="Supplier URL" testid="supplier_url">
                            <input value={draft.supplier_url || ""}
                                   onChange={e => handleField("supplier_url", e.target.value)}
                                   data-testid="k4-input-supplier-url" style={inputStyle}
                                   placeholder="https://..." />
                        </Field>
                    </div>
                    <Field label="Source URL (for desk verification)" testid="source_url">
                        <input value={draft.source_url || ""}
                               onChange={e => handleField("source_url", e.target.value)}
                               data-testid="k4-input-source-url" style={inputStyle}
                               placeholder="https://..." />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="Indicative price" testid="indicative_price">
                            <input value={draft.indicative_price || ""}
                                   onChange={e => handleField("indicative_price", e.target.value)}
                                   data-testid="k4-input-indicative-price" style={inputStyle}
                                   placeholder="Price on request" />
                        </Field>
                        <Field label="Price basis" testid="price_basis">
                            <input value={draft.price_basis || ""}
                                   onChange={e => handleField("price_basis", e.target.value)}
                                   data-testid="k4-input-price-basis" style={inputStyle}
                                   placeholder="Per person / cabin" />
                        </Field>
                    </div>
                    <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                        <textarea value={draft.concierge_whisper || ""} maxLength={280}
                                  onChange={e => handleField("concierge_whisper", e.target.value)}
                                  data-testid="k4-input-whisper"
                                  style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right" }}>
                            {(draft.concierge_whisper || "").length}/280
                        </div>
                    </Field>
                    <Field label="Best for (who is this for?)" testid="best_for">
                        <input value={draft.best_for || ""}
                               onChange={e => handleField("best_for", e.target.value)}
                               data-testid="k4-input-best-for" style={inputStyle}
                               placeholder="Polar travellers; milestone trips; wildlife" />
                    </Field>

                    {/* Safety / advisory section · the K4 differentiator vs K3 */}
                    <div style={{
                        marginTop: 18, padding: 14,
                        background: "#fff7ed", border: "1px solid #fed7aa",
                        borderRadius: 6,
                    }} data-testid="k4-safety-block">
                        <div style={{
                            fontSize: 11, fontWeight: 700, color: "#9a3412",
                            letterSpacing: "0.06em", textTransform: "uppercase",
                            marginBottom: 10,
                        }}>
                            Safety, visa, fitness
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <Field label="Safety level" testid="safety_level">
                                <select value={draft.safety_level || ""}
                                        onChange={e => handleField("safety_level", e.target.value || null)}
                                        data-testid="k4-input-safety-level" style={inputStyle}>
                                    {SAFETY_LEVELS.map(s => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Desk review status" testid="desk_review_status">
                                <select value={draft.desk_review_status || "pending"}
                                        onChange={e => handleField("desk_review_status", e.target.value)}
                                        data-testid="k4-input-desk-review" style={inputStyle}>
                                    {DESK_REVIEW_STATUSES.map(s => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <Field label="Safety notes" testid="safety_notes">
                            <textarea value={draft.safety_notes || ""} maxLength={1200}
                                      onChange={e => handleField("safety_notes", e.target.value)}
                                      data-testid="k4-input-safety-notes"
                                      style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }} />
                        </Field>
                        <Field label="Visa or permit notes" testid="visa_or_permit_notes">
                            <textarea value={draft.visa_or_permit_notes || ""} maxLength={1200}
                                      onChange={e => handleField("visa_or_permit_notes", e.target.value)}
                                      data-testid="k4-input-visa-notes"
                                      style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }} />
                        </Field>
                        <Field label="Age / fitness notes" testid="age_fitness_notes">
                            <textarea value={draft.age_fitness_notes || ""} maxLength={600}
                                      onChange={e => handleField("age_fitness_notes", e.target.value)}
                                      data-testid="k4-input-fitness-notes"
                                      style={{ ...inputStyle, minHeight: 50, fontFamily: "inherit" }} />
                        </Field>
                        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}
                               data-testid="k4-field-safety_reviewed">
                            <input type="checkbox" checked={!!draft.safety_reviewed}
                                   onChange={e => handleField("safety_reviewed", e.target.checked)}
                                   data-testid="k4-input-safety-reviewed" />
                            <span style={{ fontSize: 13, color: "#78350f" }}>
                                Safety reviewed (required for publish on extreme / desk-review rows)
                            </span>
                        </label>
                    </div>

                    <Field label="Access route (how the desk routes this)" testid="access_route">
                        <input value={draft.access_route || ""}
                               onChange={e => handleField("access_route", e.target.value)}
                               data-testid="k4-input-access-route" style={inputStyle}
                               placeholder="Official supplier / desk request" />
                    </Field>
                    <Field label="Booking rule" testid="booking_rule">
                        <input value={draft.booking_rule || ""}
                               onChange={e => handleField("booking_rule", e.target.value)}
                               data-testid="k4-input-booking-rule" style={inputStyle}
                               placeholder="Request via Concierge® only; no live price promise" />
                    </Field>

                    <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                        <button onClick={save} disabled={!dirty || busy}
                                data-testid="k4-save-btn"
                                style={{
                                    background: dirty ? "#111827" : "#9ca3af",
                                    color: "white", border: 0,
                                    padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                    fontWeight: 600, cursor: dirty ? "pointer" : "default",
                                }}>
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                        {(row.publish_status || "draft") !== "live" ? (
                            <button onClick={() => callAction("publish", "Publish")}
                                    disabled={busy}
                                    data-testid="k4-publish-btn"
                                    style={{
                                        background: "#059669", color: "white", border: 0,
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        fontWeight: 600, cursor: "pointer",
                                    }}>Publish</button>
                        ) : null}
                        {(row.publish_status || "draft") !== "archived" ? (
                            <button onClick={() => callAction("archive", "Archive")}
                                    disabled={busy}
                                    data-testid="k4-archive-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>Archive</button>
                        ) : (
                            <button onClick={() => callAction("restore", "Restore")}
                                    disabled={busy}
                                    data-testid="k4-restore-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>Restore</button>
                        )}
                    </div>
                </div>

                {/* RIGHT · image + audit */}
                <div data-testid="k4-side">
                    <Field label="Hero image" testid="image">
                        <ImageUploader
                            currentImageUrl={displayImageUrl}
                            onUpload={handleImageUpload}
                            onRemove={handleImageRemove}
                            testidPrefix="k4-image"
                        />
                        {isLegacyImage ? (
                            <div data-testid="k4-legacy-image-note" style={{
                                marginTop: 8, padding: "8px 10px",
                                background: "#fffbeb", border: "1px solid #fde68a",
                                borderRadius: 4, fontSize: 11, color: "#78350f",
                                lineHeight: 1.5,
                            }}>
                                <b>Pre-K4 legacy image</b> · this row carries an Unsplash
                                curated stock URL (<code>hero_image_url</code>). Upload a
                                new image to migrate this row to Cloudinary — the legacy
                                URL will be replaced on save.
                            </div>
                        ) : null}
                    </Field>
                    <div data-testid="k4-audit-list" style={{ marginTop: 16, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: "#374151", marginBottom: 6 }}>Audit trail</div>
                        <ul style={{ padding: 0, margin: 0, listStyle: "none", maxHeight: 240, overflowY: "auto" }}>
                            {audit.slice(0, 20).map((it, i) => (
                                <li key={i} style={{ padding: "4px 0", borderBottom: "1px dashed #eee" }}>
                                    <code style={{ fontSize: 11 }}>{it.event}</code>
                                    {" · "}
                                    <span style={{ color: "#6b7280" }}>{it.actor}</span>
                                    {" · "}
                                    <time style={{ color: "#9ca3af" }}>{(it.at || "").slice(0, 19).replace("T", " ")}</time>
                                </li>
                            ))}
                            {!audit.length ? <li style={{ color: "#9ca3af" }}>No audit history yet.</li> : null}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function EscapeSignatureCRUD() {
    const [view, setView] = useState("list");        // "list" | "create" | "detail"
    const [activeId, setActiveId] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [flagDisabled, setFlagDisabled] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true); setFlagDisabled(false);
        try {
            const params = { limit: 60 };
            if (q) params.q = q;
            if (statusFilter) params.publish_status = statusFilter;
            const r = await api.get(ROOT, { params });
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "catalogue_k4_disabled") {
                setFlagDisabled(true);
                setItems([]); setTotal(0);
            } else {
                console.error("K4 list failed:", e);
            }
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div data-testid="k4-escape-signature-crud" className="os-light-surface" style={{ padding: 24 }}>
            {view === "list" && (
                <ListView
                    items={items} total={total}
                    q={q} onSearchChange={setQ}
                    statusFilter={statusFilter} onStatusChange={setStatusFilter}
                    onOpen={(id) => { setActiveId(id); setView("detail"); }}
                    onCreate={() => setView("create")}
                    loading={loading}
                    flagDisabled={flagDisabled}
                />
            )}
            {view === "create" && (
                <CreateForm
                    onCreated={(id) => { setActiveId(id); setView("detail"); refresh(); }}
                    onCancel={() => setView("list")}
                />
            )}
            {view === "detail" && activeId && (
                <DetailView
                    rowId={activeId}
                    onBack={() => { setView("list"); refresh(); }}
                    onChanged={refresh}
                />
            )}
        </div>
    );
}
